import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { captureMessage } from '@sentry/angular-ivy';
import { Card } from '@stripe/stripe-js';
import { lastValueFrom } from 'rxjs';
import { CardFormComponent } from '../account/components/card-form/card-form.component';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { StateService } from './state.service';
import { StripeService } from './stripe.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class CardService {
  constructor(
    private config: AppConfigService,
    private http: HttpClient,
    private state: StateService,
    private stripeService: StripeService,
    private toasts: ToastService,
    private analytics: AnalyticsService
  ) {}

  public async loadCard(): Promise<Card> {
    if (!['admin', 'owner'].includes(this.state.activeTenantRole)) return;
    const resp = await lastValueFrom(
      this.http.get<{ data: { id: string; card: Card; created: number }[] }>(`${this.config.apiEndpoint}/billing/cards`)
    );
    const card = resp.data[0]?.card;
    this.state.card = card;
    this.state.paymentMethod = resp.data[0]?.id;
    this.state.cardCreatedAt = resp.data[0]?.created;
    if (card) {
      this.state.cardExpired = this.isExpired(card);
      this.state.cardExpiringSoon = this.expiresSoon(card);
    } else {
      this.state.cardExpired = false;
      this.state.cardExpiringSoon = false;
    }
    return card;
  }

  public async createCard(cardForm: CardFormComponent) {
    const card = await lastValueFrom(
      this.http.post<{ client_secret: string }>(`${this.config.apiEndpoint}/billing/cards`, {})
    );
    const resp = await this.confirmCard(card, cardForm);
    if (!resp.error) this.analytics.track('card_added');
    await this.loadCard();
    return resp;
  }

  private async confirmCard(createCardResp: { client_secret: string }, cardForm: CardFormComponent) {
    const resp = await this.stripeService.confirmCardSetup(createCardResp.client_secret, {
      payment_method: { card: cardForm.cardNumber },
    });

    if (resp.error) {
      console.warn(resp.error);
      if (!['incomplete_number'].includes(resp.error.code)) {
        captureMessage('confirm-card-failed', { level: 'info' });
      }
      this.toasts.add(resp.error.message, 'error');
    }
    return resp;
  }

  // Don't call this without first making sure the customer doesn't have an active subscription
  public async deletePaymentMethod() {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/billing/cards`, {}));
    this.state.card = undefined;
    this.state.cardExpired = false;
    this.state.cardExpiringSoon = false;
  }

  public isExpired(card: Card = this.state.card): boolean {
    if (!card) return false;
    const date = new Date(card.exp_year, card.exp_month); // 0th  day of the next month because new Date month is 0 indexed
    return date.getTime() < Date.now();
  }

  public expiresSoon(card: Card = this.state.card): boolean {
    if (!card) return false;
    const date = new Date(card.exp_year, card.exp_month);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = diff / (1000 * 60 * 60 * 24);
    return days < 30;
  }
}
